import type { FC} from 'react';
import { useState, type JSX } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { IMediaLibraryVideoModel } from '../../../../shared/models/media-library/media-library-video.model';
import { useImageSelection } from './hooks/useImageSelection';
import { useMediaLibraryVideos } from './hooks/useMediaLibraryVideos';
import VideoHeader from './components/VideoHeader';
import VideoListTab from './components/VideoListTab';
import GenerateVideoTab from './components/GenerateVideoTab';
import { useVideoCreation } from './hooks/useVideoCreator.ts';
import PreviewModal from './components/PreviewModal';
import { FormikProvider, useFormik } from 'formik';
import type { IBasicCreativeFormValues } from '@/editor/shared/components/forms/creatives/models/basic-creative-form-values.model';
import { useAppSelector } from '@/store/hooks/redux';

export interface IAnchorVideoModel {
  element: null | HTMLElement;
  item: null | IMediaLibraryVideoModel;
}

export interface IVideoLibraryProps {
  videos?: IMediaLibraryVideoModel[];
}

const MediaLibraryVideos: FC<IVideoLibraryProps> = ({ videos }): JSX.Element => {
  const { t } = useTranslation();
  const [isNextTab, setIsNextTab] = useState(1);

  const {
    previewVideoUrl,
    previewLoading,
    isVideoLoading,
    generateVideo,
    uploadGeneratedVideo,
    resetVideoCreation,
    previewModalOpen,
    mediaLibraryVideosRefetch,
    data,
    setTabValue,
    tabValue,
  } = useVideoCreation();

  const {
    selectedTemplateId,
    videoData,
    imageData,
    propertyData,
    formDataValue,
    drawerState,
    imageDataLoading,
    handleClickListItem,
    handleSearchChange,
    handleTemplateSelection,
  } = useMediaLibraryVideos(videos, data);

  const { selected, toggleSelect, isSelected } = useImageSelection(imageData);
  const creativeFormsState = useAppSelector((state) => state.creativeStepSliceReducer.creativeFormsState);
  const selectedCreativeType = useAppSelector((state) => state.creativeStepSliceReducer.selectedCreativeType);

  const savedPropertyData = creativeFormsState[drawerState.platform!][selectedCreativeType];

  const handleGenerateVideo = async () => {
    await generateVideo(formDataValue, propertyData, selected, selectedTemplateId);
  };

  const handleTabChange = (event: any, newValue: number) => {
    if (newValue === 0) return;
    setTabValue(newValue);
  };

  const handleNextTabChange = (newValue: number) => {
    if (newValue === 0) return;
    setIsNextTab(newValue);
  };

  const formik = useFormik({
    initialValues: {
      areaInUsage: savedPropertyData?.areaInUsage ?? propertyData?.propertySquareMeters ?? '',
      propertyStandard: savedPropertyData?.propertyStandard ?? propertyData?.propertyStandard ?? '',
      bedroom: savedPropertyData?.bedroom ?? propertyData?.bedroom ?? '',
      officeName: savedPropertyData?.officeName ?? propertyData?.officeTitle ?? '',
      employeePhone: savedPropertyData?.employeePhone ?? propertyData?.employeePhone ?? '',
      employeeEmail: savedPropertyData?.employeeEmail ?? propertyData?.employeeEmail ?? '',
    } as IBasicCreativeFormValues,
    validateOnChange: false,
    onSubmit: () => {},
  });

  return (
    <FormikProvider value={formik}>
      <div>
        <Grid container>
          <VideoHeader
            tabValue={tabValue}
            onTabChange={handleTabChange}
            onSearchChange={handleSearchChange}
            mediaLibraryVideosRefetch={mediaLibraryVideosRefetch}
          />
        </Grid>

        <Box style={{ borderBottom: '1px solid' }}></Box>
        {tabValue === 2 && (
          <GenerateVideoTab
            isNextTab={isNextTab}
            onNextTabChange={handleNextTabChange}
            imageData={imageData || []}
            imageDataLoading={imageDataLoading}
            selected={selected}
            isSelected={isSelected}
            onToggleSelect={toggleSelect}
            platform={drawerState.platform!}
            selectedTemplateId={selectedTemplateId}
            onTemplateSelect={handleTemplateSelection}
            onGenerateVideo={handleGenerateVideo}
          />
        )}

        {tabValue === 1 && <VideoListTab videoData={videoData} onClickListItem={handleClickListItem} />}
        <PreviewModal
          open={previewModalOpen}
          onClose={resetVideoCreation}
          previewLoading={previewLoading}
          previewVideoUrl={previewVideoUrl}
          handleDataSubmit={uploadGeneratedVideo}
          t={t}
          isVideoLoading={isVideoLoading}
        />
      </div>
    </FormikProvider>
  );
};

export default MediaLibraryVideos;
